import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControl, Typography } from '@mui/material';
import { ReleaseVersionSelect } from 'components/common/firmware/ReleaseVersionSelect';
import {
  DeviceType,
  GLOBAL_FIRMWARE_DEVICE_TYPES,
  type GetFirmwareAdminSettingsResponse,
  type GlobalFirmwareDeviceType,
  type ReleaseVersion,
} from 'api/admin/firmware/firmware';
import { useFirmwareAdminSettings } from 'api/queries/useFirmwareAdminSettings';
import { useSetFirmwareAdminSettings } from 'api/queries/useSetFirmwareAdminSettings';
import './FirmwareAdminSettings.scss';
import {
  type AdminSettingsChange,
  ConfirmAdminSettingsModal,
} from './components/ConfirmAdminSettingsModal';

type Selections = Record<GlobalFirmwareDeviceType, ReleaseVersion | null>;

const DEVICE_TYPE_LABELS: Record<GlobalFirmwareDeviceType, string> = {
  [DeviceType.DIME]: 'Dime',
  [DeviceType.PENNY]: 'Penny',
  [DeviceType.PULSE]: 'Pulse',
};

const EMPTY_SELECTIONS = GLOBAL_FIRMWARE_DEVICE_TYPES.reduce<Selections>(
  (selections, deviceType) => ({ ...selections, [deviceType]: null }),
  {} as Selections,
);

const toSelections = (settings?: GetFirmwareAdminSettingsResponse): Selections =>
  GLOBAL_FIRMWARE_DEVICE_TYPES.reduce<Selections>((selections, deviceType) => {
    const setting = settings?.[deviceType];

    return {
      ...selections,
      [deviceType]: setting ? { id: setting.releaseId, version: setting.version } : null,
    };
  }, {} as Selections);

const FirmwareAdminSettings: React.FC = () => {
  const { data: settings, isLoading, isError, refetch } = useFirmwareAdminSettings();
  const [selections, setSelections] = useState<Selections>(EMPTY_SELECTIONS);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const setAdminSettings = useSetFirmwareAdminSettings({
    successCallback: () => {
      setShowConfirmModal(false);
      refetch();
    },
  });

  useEffect(() => {
    setSelections(toSelections(settings));
  }, [settings]);

  const changedDeviceTypes = useMemo(
    () =>
      GLOBAL_FIRMWARE_DEVICE_TYPES.filter((deviceType) => {
        const selected = selections[deviceType];

        return Boolean(selected) && selected?.id !== (settings?.[deviceType]?.releaseId ?? null);
      }),
    [selections, settings],
  );

  // The API has no way to remove a global default, so a cleared selection cannot be saved.
  const clearedDeviceTypes = useMemo(
    () =>
      GLOBAL_FIRMWARE_DEVICE_TYPES.filter(
        (deviceType) => !selections[deviceType] && Boolean(settings?.[deviceType]),
      ),
    [selections, settings],
  );

  const changes = useMemo<AdminSettingsChange[]>(
    () =>
      changedDeviceTypes.map((deviceType) => ({
        deviceType: DEVICE_TYPE_LABELS[deviceType],
        fromVersion: settings?.[deviceType]?.version ?? null,
        toVersion: selections[deviceType]?.version ?? '',
      })),
    [changedDeviceTypes, selections, settings],
  );

  const hasChanges = changedDeviceTypes.length > 0;
  const isSaveDisabled =
    !hasChanges || clearedDeviceTypes.length > 0 || setAdminSettings.isLoading || isLoading;

  const handleSelectionChange = (
    deviceType: GlobalFirmwareDeviceType,
    release: ReleaseVersion | null,
  ) => {
    setSelections((current) => ({ ...current, [deviceType]: release }));
  };

  const handleCancel = () => {
    setSelections(toSelections(settings));
  };

  const handleConfirmSave = () => {
    // Only changed device types are sent — the endpoint leaves omitted ones untouched.
    const defaultReleases = changedDeviceTypes.flatMap((deviceType) => {
      const release = selections[deviceType];

      return release ? [{ deviceType, releaseId: release.id }] : [];
    });

    setAdminSettings.mutate({ defaultReleases });
  };

  return (
    <div className="firmware-admin-settings">
      {isError && (
        <Alert severity="error" className="admin-settings-alert">
          Failed to load admin settings.
        </Alert>
      )}

      <Box className="admin-settings-section">
        <Typography variant="h6" className="admin-settings-section-title">
          Global Default Firmware Release
        </Typography>
        <Typography variant="body2" color="textSecondary" className="admin-settings-section-hint">
          The release a device of each type falls back to when its store and business have no
          override configured.
        </Typography>

        {isLoading ? (
          <Box className="admin-settings-loading">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <div className="admin-settings-rows">
            {GLOBAL_FIRMWARE_DEVICE_TYPES.map((deviceType) => {
              const isCleared = clearedDeviceTypes.includes(deviceType);

              return (
                <div className="admin-settings-row" key={deviceType}>
                  <div className="admin-settings-row-label">
                    <Typography variant="subtitle1">{DEVICE_TYPE_LABELS[deviceType]}</Typography>
                  </div>
                  <div className="admin-settings-row-select">
                    <FormControl fullWidth>
                      <ReleaseVersionSelect
                        id={`admin-settings-release-${deviceType.toLowerCase()}`}
                        deviceType={deviceType}
                        selectedRelease={selections[deviceType]}
                        onChange={(release) => handleSelectionChange(deviceType, release)}
                        label="Default Release Version"
                        size="small"
                        disabled={isError || setAdminSettings.isLoading}
                      />
                    </FormControl>
                    {isCleared && (
                      <Typography variant="caption" className="admin-settings-row-error">
                        Removing a global default is not supported — pick a release version or
                        cancel.
                      </Typography>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Box>

      {(hasChanges || clearedDeviceTypes.length > 0) && (
        <div className="admin-settings-actions">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCancel}
            disabled={setAdminSettings.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowConfirmModal(true)}
            disabled={isSaveDisabled}
          >
            Save Changes
          </Button>
        </div>
      )}

      <ConfirmAdminSettingsModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
        changes={changes}
        isLoading={setAdminSettings.isLoading}
      />
    </div>
  );
};

export default FirmwareAdminSettings;
